import { useEffect, useRef } from "react";

/**
 * Focus-trap hook for modal dialogs.
 *
 * T2.1: every modal in the storefront previously had zero focus management —
 * Tab could escape into the underlying page, and closing the modal didn't
 * return focus to the trigger button. This hook implements the WCAG 2.4.3 +
 * 4.1.2 requirements shared across QuickViewModal, ExitIntentPopup,
 * SizeGuideModal, ImageLightbox, FilterBottomSheet, and the inline
 * RewardsPopup in _storefront.tsx.
 *
 * Usage:
 *   const dialogRef = useRef<HTMLDivElement>(null);
 *   useFocusTrap(dialogRef, isOpen, onClose);
 *
 *   return (
 *     <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="dialog-title">
 *       <h2 id="dialog-title">Title</h2>
 *       ...
 *     </div>
 *   );
 *
 * Behavior:
 * - On `active` transitioning to true, saves the previously-focused element
 *   and moves focus to the first focusable descendant of the container.
 * - While `active`, intercepts Tab / Shift+Tab to cycle focus within the
 *   container, and Escape to call `onClose`.
 * - On `active` transitioning to false, restores focus to the previously-
 *   focused element (typically the button that opened the modal).
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement>,
  active: boolean,
  onClose?: () => void,
) {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Save whatever was focused before the modal opened so we can restore it
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Move initial focus into the modal. Prefer the first focusable element,
    // but fall back to the container itself (with tabindex=-1 if needed).
    const focusables = getFocusableElements(container);
    if (focusables.length > 0) {
      focusables[0].focus();
    } else if (container.tabIndex !== -1) {
      // Make the container focusable as a last resort
      container.tabIndex = -1;
      container.focus();
    }

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && onClose) {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = getFocusableElements(container);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (activeEl === last || !container.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      // Restore focus to the trigger when the modal closes
      if (previouslyFocused.current && document.body.contains(previouslyFocused.current)) {
        previouslyFocused.current.focus();
      }
    };
  }, [active, containerRef, onClose]);
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selectors = [
    "a[href]:not([tabindex='-1'])",
    "button:not([disabled]):not([tabindex='-1'])",
    "input:not([disabled]):not([type='hidden']):not([tabindex='-1'])",
    "select:not([disabled]):not([tabindex='-1'])",
    "textarea:not([disabled]):not([tabindex='-1'])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");
  return Array.from(container.querySelectorAll<HTMLElement>(selectors)).filter(
    // Filter out hidden or zero-size elements which aren't actually focusable
    (el) => el.offsetParent !== null || el === container,
  );
}
